import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, RefreshCw, Calendar, Clock, MapPin, Users, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEmployee } from '../contexts/EmployeeContext';
import { Employee } from '../types/employee';
import MeetingMap from '../components/MeetingMap';
import TeamIcon from '../components/TeamIcon';
import { haversineKm, formatDistance } from '../utils/geoUtils';
import { petSpeciesIcon } from '../utils/petUtils';
import {
  selectColleague,
  suggestMeetingSlots,
  buildGoogleCalendarUrl,
  fetchMeetingHistory,
  createMeetingSuggestion,
  updateMeetingStatus,
  ColleagueMeeting,
  MeetingSlot,
} from '../utils/meetingUtils';

export default function MeetColleaguePage() {
  const { employeeProfile } = useAuth();
  const { employees } = useEmployee();

  const [matched, setMatched] = useState<Employee | null>(null);
  const [slots, setSlots] = useState<MeetingSlot[]>([]);
  const [history, setHistory] = useState<ColleagueMeeting[]>([]);
  const [currentMeeting, setCurrentMeeting] = useState<ColleagueMeeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [shuffling, setShuffling] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [bookedSlotIdx, setBookedSlotIdx] = useState<number | null>(null);

  const currentUser = employeeProfile
    ? employees.find(e => e.id === employeeProfile.slug)
    : null;

  const loadHistory = useCallback(async () => {
    if (!currentUser) return [];
    const h = await fetchMeetingHistory(currentUser.id);
    setHistory(h);
    return h;
  }, [currentUser]);

  const generateMatch = useCallback(async (hist?: ColleagueMeeting[]) => {
    if (!currentUser) return;
    setShuffling(true);
    setBookedSlotIdx(null);

    const meetingHist = hist ?? history;

    const today = new Date().toISOString().split('T')[0];
    const eligible = employees.filter(e => {
      if (e.id === currentUser.id) return false;
      if (e.meetColleagueAvailable === false) return false;
      if (e.meetColleagueUnavailableUntil && e.meetColleagueUnavailableUntil > today) return false;
      return true;
    });

    const colleague = selectColleague(currentUser.id, eligible, meetingHist);
    setMatched(colleague);

    if (colleague) {
      const userTz = currentUser.location.timezone;
      const colleagueTz = colleague.location.timezone;
      setSlots(suggestMeetingSlots(userTz, colleagueTz));

      const meeting = await createMeetingSuggestion(currentUser.id, colleague.id);
      setCurrentMeeting(meeting);
    }

    setShuffling(false);
  }, [currentUser, employees, history]);

  useEffect(() => {
    if (!currentUser || employees.length === 0) return;
    setLoading(true);
    loadHistory().then(h => {
      generateMatch(h).finally(() => setLoading(false));
    });
  }, [currentUser?.id, employees.length]);

  const handleShuffle = async () => {
    if (currentMeeting) {
      await updateMeetingStatus(currentMeeting.id, 'declined');
    }
    await generateMatch();
  };

  const handleBook = async (slot: MeetingSlot, idx: number) => {
    if (!matched || !currentUser) return;
    setBookedSlotIdx(idx);
    if (currentMeeting) {
      await updateMeetingStatus(currentMeeting.id, 'booked', slot.start);
    }
    const url = buildGoogleCalendarUrl(
      currentUser.name,
      matched.name,
      matched.email,
      slot,
    );
    window.open(url, '_blank');
  };

  const distance = currentUser && matched
    ? haversineKm(
        currentUser.location.coordinates.latitude,
        currentUser.location.coordinates.longitude,
        matched.location.coordinates.latitude,
        matched.location.coordinates.longitude,
      )
    : 0;

  if (!currentUser) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Coffee className="w-12 h-12 mx-auto text-bolt-textMuted mb-4" />
        <h1 className="text-xl font-semibold text-bolt-text mb-2">Meet a Colleague</h1>
        <p className="text-bolt-textMuted">
          You need a claimed profile to use this feature. Visit your profile to get started.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-64 bg-bolt-surface rounded" />
          <div className="h-64 bg-bolt-surface rounded-xl" />
          <div className="h-48 bg-bolt-surface rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
            <Coffee className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-bolt-text">Meet a Colleague</h1>
        </div>
        <p className="text-bolt-textMuted text-sm ml-[52px]">
          Expand your network with a virtual coffee chat. We pick someone you haven't connected with recently.
        </p>
      </div>

      {matched ? (
        <>
          {/* Map */}
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 mb-6 shadow-lg">
            <div className="aspect-[2.76/1]">
              <MeetingMap
                userLat={currentUser.location.coordinates.latitude}
                userLon={currentUser.location.coordinates.longitude}
                userLabel={`${currentUser.name.split(' ')[0]} - ${currentUser.location.city}`}
                colleagueLat={matched.location.coordinates.latitude}
                colleagueLon={matched.location.coordinates.longitude}
                colleagueLabel={`${matched.name.split(' ')[0]} - ${matched.location.city}`}
              />
            </div>
            <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full">
              {formatDistance(distance)} apart
            </div>
          </div>

          {/* Colleague card + actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Colleague info */}
            <div className="md:col-span-1 bg-bolt-surface border border-bolt-border rounded-xl p-5">
              <Link to={`/employee/${matched.id}`} className="block group">
                <img
                  src={matched.avatar}
                  alt={matched.name}
                  className="w-20 h-20 rounded-full mx-auto mb-3 object-cover ring-2 ring-bolt-border group-hover:ring-teal-500 transition-all"
                />
                <h2 className="text-center font-semibold text-bolt-text group-hover:text-teal-600 transition-colors">
                  {matched.name}
                </h2>
              </Link>
              <p className="text-center text-sm text-bolt-textMuted mt-1">{matched.role}</p>
              <p className="text-center text-xs text-bolt-textSubtle mt-0.5">{matched.department}</p>

              <div className="mt-4 space-y-2 text-xs text-bolt-textMuted">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{matched.location.city}, {matched.location.country}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{matched.location.timezone.replace(/_/g, ' ')}</span>
                </div>
              </div>

              {/* Conversation starters */}
              {((matched.teams && matched.teams.length > 0) || (matched.pets && matched.pets.length > 0)) && (
                <div className="mt-4 pt-4 border-t border-bolt-border">
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <MessageCircle className="w-3 h-3 text-teal-500" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-bolt-textSubtle">Conversation starters</span>
                  </div>

                  {matched.teams && matched.teams.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1.5">
                        {matched.teams.map(team => (
                          <Link
                            key={team.id}
                            to={`/team/${team.id}`}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-bolt-bg border border-bolt-border text-[11px] text-bolt-textMuted hover:border-teal-500/50 hover:text-bolt-text transition-colors"
                          >
                            <TeamIcon team={team} size={12} />
                            {team.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {matched.pets && matched.pets.length > 0 && (
                    <div className="space-y-1.5">
                      {matched.pets.map(pet => {
                        const Icon = petSpeciesIcon(pet.species);
                        return (
                          <div key={pet.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-bolt-bg border border-bolt-border">
                            {pet.photoUrl ? (
                              <img src={pet.photoUrl} alt={pet.name} className="w-5 h-5 rounded-full object-cover" />
                            ) : (
                              <Icon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <span className="text-[11px] font-medium text-bolt-text">{pet.name}</span>
                              <span className="text-[10px] text-bolt-textSubtle ml-1">({pet.species})</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleShuffle}
                disabled={shuffling}
                className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-bolt-border text-bolt-text hover:bg-bolt-surface/80 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${shuffling ? 'animate-spin' : ''}`} />
                {shuffling ? 'Finding...' : 'Someone else'}
              </button>
            </div>

            {/* Time slots */}
            <div className="md:col-span-2 bg-bolt-surface border border-bolt-border rounded-xl p-5">
              <h3 className="font-semibold text-bolt-text mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-teal-500" />
                Suggested Times
              </h3>
              <p className="text-xs text-bolt-textMuted mb-4">
                Slots that work for both your timezones. Click to create a calendar event.
              </p>

              <div className="space-y-2">
                {slots.map((slot, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      bookedSlotIdx === idx
                        ? 'border-teal-500 bg-teal-500/10'
                        : 'border-bolt-border hover:border-teal-500/50 hover:bg-bolt-surface/50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-bolt-text">{slot.dayLabel}</div>
                      <div className="text-xs text-bolt-textMuted mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />
                          You: {slot.userTime}
                        </span>
                        <span className="mx-2 text-bolt-textSubtle">|</span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                          {matched.name.split(' ')[0]}: {slot.colleagueTime}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleBook(slot, idx)}
                      className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        bookedSlotIdx === idx
                          ? 'bg-teal-600 text-white'
                          : 'bg-teal-500 hover:bg-teal-600 text-white'
                      }`}
                    >
                      {bookedSlotIdx === idx ? 'Booked!' : 'Book'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Meeting history */}
          <div className="border border-bolt-border rounded-xl overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-bolt-text hover:bg-bolt-surface/50 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-bolt-textMuted" />
                Meeting History ({history.length})
              </span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showHistory && history.length > 0 && (
              <div className="border-t border-bolt-border divide-y divide-bolt-border">
                {history.slice(0, 20).map(meeting => {
                  const otherId = meeting.requesterId === currentUser.id ? meeting.matchedId : meeting.requesterId;
                  const other = employees.find(e => e.id === otherId);
                  if (!other) return null;
                  return (
                    <div key={meeting.id} className="flex items-center gap-3 px-5 py-3">
                      <img
                        src={other.avatar}
                        alt={other.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/employee/${other.id}`}
                          className="text-sm font-medium text-bolt-text hover:text-teal-600 transition-colors"
                        >
                          {other.name}
                        </Link>
                        <div className="text-xs text-bolt-textSubtle">
                          {new Date(meeting.suggestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        meeting.status === 'booked'
                          ? 'bg-teal-500/10 text-teal-600'
                          : meeting.status === 'declined'
                          ? 'bg-red-500/10 text-red-500'
                          : 'bg-amber-500/10 text-amber-600'
                      }`}>
                        {meeting.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {showHistory && history.length === 0 && (
              <div className="px-5 py-6 text-center text-sm text-bolt-textMuted border-t border-bolt-border">
                No meetings yet. This will be your first!
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-16 bg-bolt-surface rounded-xl border border-bolt-border">
          <Users className="w-12 h-12 mx-auto text-bolt-textMuted mb-4" />
          <h2 className="text-lg font-semibold text-bolt-text mb-2">No colleagues available</h2>
          <p className="text-sm text-bolt-textMuted max-w-md mx-auto">
            It looks like there are no colleagues available for matching right now.
            Check back later or ask your team to enable meeting availability in their profiles.
          </p>
        </div>
      )}
    </div>
  );
}
